import React, { useState, createContext, useContext, ReactNode } from 'react';
export type PersonaId = 'sme' | 'accountant' | 'cfo' | null;
export interface PersonaMeta {
  id: Exclude<PersonaId, null>;
  label: string;
  shortLabel: string;
  audience: string;
  description: string;
  primaryPain: string;
  primaryService: string;
  formPrefill: string;
}
export const personas: Record<Exclude<PersonaId, null>, PersonaMeta> = {
  sme: {
    id: 'sme',
    label: 'Small business owner',
    shortLabel: 'SME owner',
    audience: '5 to 50 staff. Retail, hospitality, trades, medical, gym.',
    description: 'You need books that are right, payroll on time, BAS handled, and a real person watching the cash flow.',
    primaryPain: 'Payroll, STP and awards are eating 6 to 10 hours a fortnight. The books fall behind. BAS is stressful. Numbers arrive too late to act on.',
    primaryService: 'Bookkeeping and Payroll, with CFO-level supervision included.',
    formPrefill: 'Bookkeeping and Payroll for my business'
  },
  accountant: {
    id: 'accountant',
    label: 'Accounting firm principal',
    shortLabel: 'Accounting firm',
    audience: 'Firms that need white-label or overflow capacity.',
    description: 'You need capacity without hiring, your branding kept front, and quality that holds under your name.',
    primaryPain: 'You cannot hire fast enough. Busy season is overflowing. Quality cannot slip. You want help inside your stack, not a new platform to learn.',
    primaryService: 'Bespoke white-label bookkeeping for accounting firms.',
    formPrefill: 'White-label or overflow support for my firm'
  },
  cfo: {
    id: 'cfo',
    label: 'Scaling founder or GM',
    shortLabel: 'Virtual CFO',
    audience: 'Growing businesses that need strategy, not just data entry.',
    description: 'You need forecasting, budgeting, funding strategy and risk visibility, without paying for a full-time CFO.',
    primaryPain: 'You are flying blind on cash flow, forecasting and funding. Decisions are made on gut. A full-time CFO costs more than you can justify.',
    primaryService: 'Virtual CFO. CFO thinking without the CFO salary.',
    formPrefill: 'Virtual CFO support for my growing business'
  }
};
interface PersonaContextValue {
  persona: PersonaId;
  setPersona: (p: PersonaId) => void;
}
const PersonaContext = createContext<PersonaContextValue>({
  persona: null,
  setPersona: () => {}
});
export function PersonaProvider({
  children
}: {
  children: ReactNode;
}) {
  const [persona, setPersona] = useState<PersonaId>(null);
  return <PersonaContext.Provider value={{
    persona,
    setPersona
  }} data-id="element-338">
      {children}
    </PersonaContext.Provider>;
}
export const usePersona = () => useContext(PersonaContext);